import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import DatePicker from './DatePicker';
import useCategories from '../data/useCategories';
import { GOLD, GOLD_LT, CREAM, INK, MAPS_URL } from '../theme';
import './BookAppointment.css';

// Offered 'Casual' and 'Luxury Couture', neither of which is a category the
// atelier actually publishes — so an enquiry could arrive tagged with
// something that maps to nothing. Driven off the live catalogue instead, with
// the old list as a fallback while it loads.
const FALLBACK_CATEGORIES = ['Bridal', 'Pret', 'Formal', 'Party Wear'];
const OTHER_OPTION = 'Something else';

const FORM_NAME = 'appointment-request';

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&');

const labelStyle = { display: 'block', fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: GOLD, marginBottom: 10 };
// A bare bottom rule gave the field almost no visible target — on a phone it
// read as a divider, not somewhere to type. A faint inset panel plus the rule
// keeps the couture restraint while making the tappable area obvious.
const inputStyle = { width: '100%', background: 'rgba(201,162,75,.05)', border: 'none', borderBottom: '1px solid rgba(201,162,75,.35)', color: CREAM, fontFamily: "'Cormorant Garamond', serif", fontSize: 18, padding: '14px 12px', boxSizing: 'border-box' };
const errorTextStyle = { color: '#e8a0a0', fontSize: 14, margin: '8px 0 0', letterSpacing: '.02em' };

// `label` used to render with neither htmlFor nor the input nested inside it,
// so every field on this page was announced as an unlabelled edit box. The id
// is threaded through to the control and the error message is wired up via
// aria-describedby so it is read out with the field, not stranded at the
// bottom of the form.
function Field({ id, label, children, style, required, error }) {
  return (
    <div className="ij-field" style={{ flex: '1 1 260px', ...style }}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required && <span aria-hidden="true" style={{ color: GOLD_LT, marginLeft: 6 }}>*</span>}
        {required && <span className="ij-sr-only"> (required)</span>}
      </label>
      {children}
      {error && <p id={`${id}-error`} style={errorTextStyle}>{error}</p>}
    </div>
  );
}

export default function BookAppointment() {
  const [searchParams] = useSearchParams();
  const dressName = searchParams.get('dress') || '';
  const categorySlug = searchParams.get('categorySlug') || '';
  const dressSlug = searchParams.get('dressSlug') || '';
  const dressHref = categorySlug && dressSlug ? `/categories/${categorySlug}/${dressSlug}` : null;
  const dressInterest = dressName ? `${dressName}${dressHref ? ` — ${window.location.origin}${dressHref}` : ''}` : '';

  const { categories: liveCategories } = useCategories();
  const CATEGORIES = (liveCategories.length > 0
    ? liveCategories.map((c) => c.name)
    : FALLBACK_CATEGORIES
  ).concat(OTHER_OPTION);

  const [gateLoading, setGateLoading] = useState(true);
  const [fullyBooked, setFullyBooked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({ name: '', email: '', phone: '', preferredDate: '', category: CATEGORIES[0], notes: '', dressInterest, 'bot-field': '' });

  // The initial value is seeded from the fallback list, so once the real
  // catalogue arrives the selected option can be one the <select> no longer
  // offers — which renders as a blank control. Re-point it at the first real
  // category in that case.
  useEffect(() => {
    if (liveCategories.length === 0) return;
    setForm((f) => (CATEGORIES.includes(f.category) ? f : { ...f, category: CATEGORIES[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCategories]);

  useEffect(() => {
    fetch('/.netlify/functions/booking-count')
      .then((r) => r.json())
      .then((data) => setFullyBooked(data.remaining <= 0))
      .catch(() => setFullyBooked(false))
      .finally(() => setGateLoading(false));
  }, []);

  const update = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    // Clear the message the moment they start correcting it, rather than
    // leaving a stale error sitting under a field they have already fixed.
    setFieldErrors((errs) => (errs[key] ? { ...errs, [key]: undefined } : errs));
  };

  // Native validation only produces a browser tooltip that says "Please fill
  // out this field" and vanishes on the next click. These messages name the
  // field and say what is wrong, and they stay put next to the input.
  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Please tell us your name so we know who to expect.';
    if (!form.email.trim()) errs.email = 'We need an email to send your confirmation to.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'That email address looks incomplete — check for a missing @ or domain.';
    if (form.phone.trim() && !/^[+()\-\s\d]{7,}$/.test(form.phone.trim())) errs.phone = 'Please enter a reachable phone number, including country code.';
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form['bot-field']) return; // honeypot tripped — silently drop

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError(null);
      // Send focus to the first problem so a keyboard or screen-reader user
      // is taken to it instead of having to hunt back up the form.
      const firstId = ['name', 'email', 'phone'].find((k) => errs[k]);
      if (firstId) {
        const el = document.getElementById(`ij-${firstId}`);
        if (el) el.focus();
      }
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    setError(null);
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': FORM_NAME, ...form }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Submission failed');
        setSubmitted(true);
        fetch('/.netlify/functions/booking-count', { method: 'POST' }).catch(() => {});
      })
      .catch(() => setError('Something went wrong sending your request — please try again, or reach us directly below.'))
      .finally(() => setSubmitting(false));
  };

  return (
    <div style={{ fontFamily: "'Cormorant Garamond', serif", background: INK, color: '#efe4d2', minHeight: '100vh' }}>
      <Nav />
      <main id="ij-main">

      <section style={{ padding: 'clamp(64px,10vh,110px) clamp(20px,4vw,60px) clamp(64px,10vh,120px)', maxWidth: 760, margin: '0 auto' }}>
        <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '.36em', textTransform: 'uppercase', color: GOLD, marginBottom: 14 }}>Reserve Your Time</div>
        <h1 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontStyle: 'italic', fontSize: 'clamp(38px,5.4vw,64px)', lineHeight: 1, margin: '0 0 20px', color: CREAM }}>Book an appointment</h1>
        <p style={{ fontSize: 18, lineHeight: 1.7, color: 'rgba(239,228,210,.75)', maxWidth: 560, marginBottom: dressName ? 24 : 56 }}>
          Share a few details about your occasion and preferred timing, and our atelier will reach out within 48 hours to confirm your fitting.
        </p>

        {dressName && (
          <div style={{ border: '1px solid rgba(201,162,75,.35)', padding: '14px 20px', marginBottom: 32, fontSize: 15, color: 'rgba(239,228,210,.8)' }}>
            Enquiring about: <strong style={{ color: CREAM }}>{dressName}</strong>
            {dressHref && (
              <>
                {' — '}
                <Link to={dressHref} style={{ color: GOLD_LT, textDecoration: 'none' }}>View piece</Link>
              </>
            )}
          </div>
        )}

        {gateLoading && (
          <p style={{ color: 'rgba(239,228,210,.55)', fontStyle: 'italic' }}>Loading…</p>
        )}

        {!gateLoading && fullyBooked && (
          <div style={{ border: '1px solid rgba(201,162,75,.35)', padding: 'clamp(28px,4vw,44px)' }}>
            <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontSize: 28, color: GOLD_LT, margin: '0 0 16px' }}>We're fully booked this month</h2>
            <p style={{ fontSize: 17, lineHeight: 1.7, color: 'rgba(239,228,210,.75)', marginBottom: 20 }}>
              We've reached our appointment request limit for this month. Please reach out to us directly and we'll do our best to accommodate you.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 17 }}>
              <a href="mailto:hello@ijcouture.com" style={{ color: GOLD_LT, textDecoration: 'none' }}>hello@ijcouture.com</a>
              <a href="tel:+920000000000" style={{ color: GOLD_LT, textDecoration: 'none' }}>+92 000 0000000</a>
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" style={{ color: GOLD_LT, textDecoration: 'none' }}>F-7 Markaz, Islamabad — Get Directions →</a>
            </div>
          </div>
        )}

        {!gateLoading && !fullyBooked && submitted && (
          <div style={{ border: '1px solid rgba(201,162,75,.35)', padding: 'clamp(28px,4vw,44px)' }}>
            <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontSize: 28, color: GOLD_LT, margin: '0 0 16px' }}>Thank you, {form.name.split(' ')[0] || 'friend'}.</h2>
            <p style={{ fontSize: 17, lineHeight: 1.7, color: 'rgba(239,228,210,.75)' }}>
              Your request has been received. Our atelier will contact you within 48 hours to confirm your appointment.
            </p>
          </div>
        )}

        {!gateLoading && !fullyBooked && !submitted && (
          <form name={FORM_NAME} noValidate onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
              <Field id="ij-name" label="Full Name" required error={fieldErrors.name}>
                <input
                  id="ij-name"
                  name="name"
                  style={inputStyle}
                  type="text"
                  autoComplete="name"
                  required
                  aria-invalid={fieldErrors.name ? 'true' : undefined}
                  aria-describedby={fieldErrors.name ? 'ij-name-error' : undefined}
                  value={form.name}
                  onChange={update('name')}
                />
              </Field>
              <Field id="ij-email" label="Email" required error={fieldErrors.email}>
                <input
                  id="ij-email"
                  name="email"
                  style={inputStyle}
                  type="email"
                  autoComplete="email"
                  required
                  aria-invalid={fieldErrors.email ? 'true' : undefined}
                  aria-describedby={fieldErrors.email ? 'ij-email-error' : undefined}
                  value={form.email}
                  onChange={update('email')}
                />
              </Field>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
              <Field id="ij-phone" label="Phone" error={fieldErrors.phone}>
                <input
                  id="ij-phone"
                  name="phone"
                  style={inputStyle}
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  aria-invalid={fieldErrors.phone ? 'true' : undefined}
                  aria-describedby={fieldErrors.phone ? 'ij-phone-error' : undefined}
                  value={form.phone}
                  onChange={update('phone')}
                />
              </Field>
              <Field id="ij-date" label="Preferred Date">
                <DatePicker inputId="ij-date" value={form.preferredDate} onChange={(iso) => setForm((f) => ({ ...f, preferredDate: iso }))} />
              </Field>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <Field id="ij-category" label="Occasion / Category" style={{ flex: 'none' }}>
                <select id="ij-category" name="category" style={inputStyle} value={form.category} onChange={update('category')}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field id="ij-notes" label="Notes" style={{ flex: 'none' }}>
                <textarea id="ij-notes" name="notes" style={{ ...inputStyle, borderBottom: '1px solid rgba(201,162,75,.35)', resize: 'vertical', minHeight: 90 }} value={form.notes} onChange={update('notes')} placeholder="Wedding date, city, styles you have in mind…" />
              </Field>
            </div>

            {form.dressInterest && <input type="hidden" name="dressInterest" value={form.dressInterest} />}

            <div className="ij-honeypot" aria-hidden="true">
              <label>Don't fill this out: <input tabIndex={-1} autoComplete="off" name="bot-field" value={form['bot-field']} onChange={update('bot-field')} /></label>
            </div>

            {error && <p role="alert" style={{ color: '#e8a0a0', fontSize: 15 }}>{error}</p>}

            <button className="ij-submit-btn" type="submit" disabled={submitting} style={{ alignSelf: 'flex-start', fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '15px 34px', background: GOLD, color: INK, border: 'none', cursor: 'pointer' }}>
              {submitting ? 'Sending…' : 'Submit Request'}
            </button>
          </form>
        )}
      </section>

      </main>
      <Footer />
    </div>
  );
}
